import React, { useEffect, useState } from 'react'
import { Modal, Spin } from 'antd'
import {
  SyncConfirmBody,
  SyncConfirmList,
  SyncingOverlayBody,
  SyncingOverlayStep,
  SyncingOverlayTitle,
} from './ClientBillingProfileManagement.styles'
import { SYNCING_STEPS } from './hubspotSync.helpers'

const STEP_INTERVAL_MS = 900

interface HubspotSyncModalProps {
  open: boolean
  isRunning: boolean
  onStart: () => void
  onCancel: () => void
}

/**
 * Confirms the run, then reports it. The steps are indicative rather than
 * reported: the endpoint answers once, at the end, with no progress of its own.
 * The last one therefore holds until the response lands.
 */
const HubspotSyncModal = ({
  open,
  isRunning,
  onStart,
  onCancel,
}: HubspotSyncModalProps) => {
  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    if (!isRunning) {
      setStepIndex(0)
      return
    }
    const timer = setInterval(() => {
      setStepIndex((curr) => Math.min(curr + 1, SYNCING_STEPS.length - 1))
    }, STEP_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [isRunning])

  return (
    <Modal
      open={open}
      title={isRunning ? null : 'Sync billing profiles from HubSpot'}
      footer={isRunning ? null : undefined}
      okText="Start sync"
      onOk={onStart}
      onCancel={onCancel}
      closable={!isRunning}
      maskClosable={false}
      keyboard={!isRunning}
      destroyOnClose
      width={560}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
    >
      {isRunning ? (
        <SyncingOverlayBody>
          <Spin size="large" />
          <SyncingOverlayTitle>Syncing with HubSpot</SyncingOverlayTitle>
          <SyncingOverlayStep aria-live="polite">
            {SYNCING_STEPS[stepIndex]}
          </SyncingOverlayStep>
        </SyncingOverlayBody>
      ) : (
        <SyncConfirmBody>
          <p>
            This compares HubSpot companies, contacts and deals with the billing
            profiles here.
          </p>
          <SyncConfirmList>
            <li>New companies need a due date before they can be created.</li>
            <li>Existing companies show what HubSpot would change.</li>
            <li>
              Dropped addresses, and companies with no SureCo client, are held
              for review.
            </li>
          </SyncConfirmList>
          <p>Nothing is saved until you submit.</p>
        </SyncConfirmBody>
      )}
    </Modal>
  )
}

export { HubspotSyncModal }
